import {
  AfterViewInit,
  ChangeDetectorRef,
  Component, ElementRef,
  inject,
  model,
  OnInit, signal,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  ButtonComponent,
  ButtonOptions,
  DateComponent, DateOptions,
  FormComponent,
  ModalComponent,
  ModalOptions
} from "@shared/components";
import {DropdownComponent, DropdownOptions} from "@shared/components/dropdown/dropdown.component";
import {NgOptimizedImage} from "@angular/common";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NgForm,
  ReactiveFormsModule, ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {APIService, DataService, ToastService} from '@core/services';
import {PatientService} from '@core/services/patient.service';
import {HTTPMethods, InputTypes, ModalTypes} from '@core/enums';
import {HttpHeaders} from '@angular/common/http';
import {assets, FILTER_TEXT_FOR_TEXTAREA} from '@core/constants';
import {VaccinesSearchComponent} from '@shared/components/dropdown/search/vaccines-search/vaccines-search.component';
import {Coding} from '@core/classes/other/coding';
import {IllnessSearchComponent} from '@shared/components/dropdown/search/illness-search/illness-search.component';
import {TextAreaComponent, TextAreaOptions} from '@shared/components/text-area/text-area.component';
import moment, {Moment} from 'moment';
import {ResponseSingle} from '@core/classes/other/response';
import {PastIllness} from '@core/classes/tables/past-illness';
import {checkDate} from '@core/helpers';

@Component({
  selector: 'ncp-add-past-illness',
  imports: [
    ButtonComponent,
    DateComponent,
    DropdownComponent,
    ModalComponent,
    NgOptimizedImage,
    ReactiveFormsModule,
    TextAreaComponent
  ],
  templateUrl: './add-past-illness.component.html',
  styleUrl: './add-past-illness.component.scss',
})
export class AddPastIllnessComponent implements OnInit, AfterViewInit {
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('piFrm') protected illnessFormEl?: NgForm;
  @ViewChild('addIllnessButton') public addIllnessButton!: ButtonComponent;
  @ViewChild('onSetDate') public onSetDate!: DateComponent;
  @ViewChild('endDate') public endDate!: DateComponent;

  @ViewChild( 'illnessDesc') public illnessDesc!: DropdownComponent<Coding>;

  @ViewChild('illnessTemplate') public illnessTemplate!: TemplateRef<Coding>;

  protected readonly toastService = inject(ToastService);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private apiService = inject(APIService);
  private patientService = inject(PatientService);
  private dataService = inject(DataService);

  protected data = model<any>();

  private _params = {};
  protected form: FormGroup = new FormGroup({
    coding_id: new FormControl<number | string | undefined>(undefined, [Validators.required]),
    onset_date: new FormControl<string>('', [Validators.required]),
    end_date: new FormControl<string>({value: '', disabled: true}, [Validators.required]),
    resolution_circumstances: new FormControl<string>('', [Validators.required]),
  }, {
    validators: [this.formValidator()]
  });

  getFormControl = (name: string) => {
    return this.form.get(name) as FormControl;
  }

  formValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};

      let codingId = (control as FormGroup).controls['coding_id'];
      let onSetDate = (control as FormGroup).controls['onset_date'];
      let endDate = (control as FormGroup).controls['end_date'];
      let resolution = (control as FormGroup).controls['resolution_circumstances'];

      let patientDOB = moment(this.patientService.data?.dob, ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY'], true);
      let today = moment();

      if(codingId.value === undefined || codingId.value === null) {
        codingId.setErrors({pi_codingIdInvalid: true});
        errors['pi_codingIdInvalid'] = true;
      }

      if(!checkDate(onSetDate.value).isValid()) {
        onSetDate.setErrors({pi_onSetDateFormatInvalid: true});
        errors['pi_onSetDateInvalid'] = true;
      } else if(!checkDate(endDate.value).isValid()) {
        endDate.setErrors({pi_endDateFormatInvalid: true});
        errors['pi_endDateInvalid'] = true;
      } else {
        if(!checkDate(onSetDate.value).isSameOrBefore(checkDate(endDate.value))) {
          onSetDate.setErrors({pi_onSetDateBeforeEndDate: true});
          endDate.setErrors({pi_endDateAfterOnSetDate: true});
          errors['pi_onSetNotAfterEnd'] = true;
        } else {
          if(checkDate(onSetDate.value).isSameOrBefore(patientDOB)) {
            onSetDate.setErrors({pi_onSetDateAfterDOB: true});
            errors['pi_startNotAfterPatientDoB'] = true;
          } else if(checkDate(endDate.value).isSameOrAfter(today)) {
            endDate.setErrors({pi_endDateBeforeToday: true});
            errors['pi_endDateNotBeforeToday'] = true;
          } else {
            onSetDate.setErrors(null);
            endDate.setErrors(null);
          }
        }
      }

      if(resolution.value === undefined || resolution.value === null || resolution.value === '') {
        errors['pi_resolutionInvalid'] = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }
  }

  public btnPIOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'ndaBtn',
      tooltip: 'add_illness',
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'modal',
      type: ModalTypes.form,
      form: FormComponent,
      title: 'add_illness',
      options: {
        animation: true,
        size: 'xl',
        fullscreen: "lg",
        backdrop: 'static',
        keyboard: false,
      },
      onClose: () => {this.onFormReset()}
    })
  );

  protected illnessDescDDOptions = signal<DropdownOptions<Coding>>(
    new DropdownOptions<Coding>({
      id: 'illnessDescription',
      name: 'illnessDescription',
      label: {
        id: "illness_desc_label",
        text: 'illness_description',
      },
      api: {
        description: {
          path: '/get-all_codings',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          body: {
            oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.5',
            description: ''
          }
        },
        code: {
          path: '/get-icd10',
          headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
          body: {
            description: ''
          }
        }
      },
      required: true,
      searchComponent: IllnessSearchComponent,
      formControl: this.form.get('coding_id') as FormControl
    })
  );

  protected onSetDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'piOnSetDate',
      label: {
        text: 'on_set',
      },
      name: 'piOnSetDate',
      initialValue: '',
      formControl: this.form.get('onset_date') as FormControl,
      minDate: moment(this.patientService.data?.dob).format('YYYY-MM-DD'),
      maxDate: moment().format('YYYY-MM-DD'),
    })
  );

  protected endDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'piEndDate',
      label: {
        text: 'end_date',
      },
      name: 'piEndDate',
      initialValue: '',
      formControl: this.form.get('end_date') as FormControl,
      minDate: moment(this.patientService.data?.dob).format('YYYY-MM-DD'),
      maxDate: moment().format('YYYY-MM-DD'),
      disabled: true
    })
  );



  protected resolutionsOptions = signal<TextAreaOptions>(
    new TextAreaOptions({
      id: 'resolution_circumstances',
      label: {
        text: 'resolution_circumstances',
      },
      name: 'resolution_circumstances',
      formControl: this.getFormControl('resolution_circumstances'),
      initialValue: '',
      pattern: FILTER_TEXT_FOR_TEXTAREA
    })
  );

  ngOnInit() {
    this.btnPIOptions().onClick = (event) => {
      this.mod.options().onReset = this.onFormReset;
      let illnessOptions = this.illnessDescDDOptions();
      illnessOptions.itemTemplate = this.illnessTemplate;
      this.illnessDescDDOptions.set(illnessOptions);
      this.illnessDesc.detectChanges();
      this.addIllnessButton.blur();
      this.mod.open();
    };
    //this.onSetDateOptions().formControl = (this.form.get('onset_date') as FormControl);
    //this.endDateOptions().formControl = (this.form.get('end_date') as FormControl);
    //this.onSetDateOptions().onInput = this.onOnSetDateInput;
    this.modOptions().onSave = this.onSave;

    this.form.valueChanges.subscribe(value => {
      if(
        value?.onset_date &&
        checkDate(value?.onset_date, true).isValid() &&
        this.form.get('onset_date')?.valid
      ) {
        console.log(
          'value = ' + value?.onset_date,
          'check = ' + checkDate(value?.onset_date, true).isValid(),
          'onset valid = ' + this.form.get('onset_date')?.valid,
          'end disabled = ' + this.form.get('end_date')?.disabled
        )
        this.endDate!.minDate! = {
          year: parseInt(moment(value?.onset_date).format('YYYY'), 10),
          month: parseInt(moment(value?.onset_date).format('M'), 10),
          day: parseInt(moment(value?.onset_date).format('D'), 10),

        }
        if(this.form.get('end_date')?.disabled) {
          this.endDate?.enable();
        }
      }

      if(
        (value?.onset_date &&
        !checkDate(value?.onset_date, true).isValid() &&
        !this.form.get('onset_date')?.valid) &&
        this.form.get('end_date')?.enabled
      ) {
        console.log(
          'value = ' + value?.onset_date,
          'check = ' + checkDate(value?.onset_date, true).isValid(),
          'onset valid = ' + this.form.get('onset_date')?.valid,
          'end enabled = ' + this.form.get('end_date')?.enabled
        )
        this.endDate?.disable();
      }

      if(
        value?.end_date &&
        checkDate(value?.end_date, true).isValid() &&
        this.form.get('end_date')?.valid
      ) {
        console.log(
          'value = ' + value?.onset_date,
          'check = ' + checkDate(value?.onset_date, true).isValid(),
          'onset valid = ' + this.form.get('onset_date')?.valid,
          'end disabled = ' + this.form.get('end_date')?.disabled
        )
        this.onSetDate!.maxDate! = {
          year: parseInt(moment(value?.end_date).format('YYYY'), 10),
          month: parseInt(moment(value?.end_date).format('M'), 10),
          day: parseInt(moment(value?.end_date).format('D'), 10),

        }
      }
    });
  }

  onOnSetDateInput = (el: HTMLInputElement) => {
    console.log('date = ' + el.value);
    if(checkDate(el.value, true).isValid()) {
      console.log('date valid')
      this.endDate.enable();
    } else {
      console.log('date invalid')
      this.endDate.disable();
    }

    return el.value;
  }

  onFormReset = () => {
    console.log('on reset form');
    this.form.reset();
    this.onSetDate.reset();
    this.endDate.reset();
    this.modOptions().errors = undefined;
    this.illnessDesc.clearSelection();

    this.modOptions().errors = [];
  }

  ngAfterViewInit(): void {

  }

  onSave = () => {
    this.illnessFormEl?.ngSubmit.emit();
  }

  validate = (): boolean => {
    Object.keys(this.form.controls).forEach((control) => {
      this.form.controls[control].markAsTouched();
    })

    return true;
  }

  onFormSubmit = () => {
    console.log(this.form.value);
    if(this.validate() && this.form.invalid) {
      console.log('this.form.is.invalid');
      this.modOptions().errors = this.form.errors!;
      this.mod.detectChanges();
      return;
    }

    this.modOptions().disableOnSave = true;
    let body = {
      onset_date: (this.form.get('onset_date') as AbstractControl).value,
      end_date: (this.form.get('end_date') as FormControl).value,
      coding_id: this.illnessDescDDOptions().value!.coding_id,
      resolution_circumstances: (this.form.get('resolution_circumstances') as FormControl).value,
      status: 1,
      version_id: this.patientService.version!.new,
    }

    this.apiService.call({
      path: '/illness',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      type: HTTPMethods.POST,
      body: body
    })?.subscribe((res: ResponseSingle<PastIllness>) => {
      if(res && res.status?.toString() === '1') {
        let data = this.data()?.tblOptions().data;
        data.unshift(res.data);
        if (this.data && this.data() && this.data().tbl && this.data().tbl.tableData) {
          this.data()!.tbl!.tableData! = data;
        }
        this.mod.close();
      }
    })
  }

  protected readonly assets = assets;
}
